import styled from '@emotion/styled';
import Breadcrumbs from 'aquilla/core/molecules/Breadcrumbs';
import PageHeader from 'aquilla/core/organisms/PageHeader';
import { mediaQuery, Spacings } from '@styles/index';
import { useRef, useState } from 'react';
import useClickAwayListener from '@hooks/useClickAwayListener';
import { useIsMobile } from '@stores/AppCommonData';
import JumpToSelector from './JumpToSelector';
import WebFilterContainer from './WebFilterContainer';
import JumpToWeek from './JumpToWeek';
import MwebBottomSheet from './MwebBottomSheet';

const filterMap = {
  sessions: 0,
  subjects: 1
};

const HeaderContent = styled.div`
  padding-bottom: ${Spacings.SPACING_6B};
  border-bottom: ${Spacings.SPACING_1} solid var(--color-i-divider);
  ${mediaQuery.uptoMobile} {
    margin-left: ${Spacings.SPACING_0B};
    width: 100%;
  }
`;

const WebFilterWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  width: 1136px;
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const PageHeaderContainer = styled.div`
  width: 1136px;
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;
const Container = styled.div`
  background-color: var(--color-base-1);
  display: flex;
  flex-direction: column;
  padding-top: 112px;
  width: 100%;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 2;
  .aquilla-typography {
    min-width: max-content;
  }
  .store-batch-header {
    border: none;
    margin: ${Spacings.SPACING_0B};
    & > div {
      ${mediaQuery.uptoMobile} {
        margin-bottom: ${Spacings.SPACING_3B};
      }
    }
  }

  ${mediaQuery.uptoMobile} {
    align-items: flex-start;
    padding-right: ${Spacings.SPACING_4B};
    padding-left: ${Spacings.SPACING_4B};
    padding-top: 64px;
    position: absolute;
  }
`;

const RecordedBatchHeader = ({
  batchUID,
  sessionType,
  selectedSubject,
  selectedWeek,
  handleSessionTypeChange,
  handleSubjectChange,
  handleJumpToWeek,
  filterOptions,
  weekDetails
}) => {
  const [openBottomSheet, setOpenBottomSheet] = useState(false);
  const [filterNumberSelected, setFilterNumberSelected] = useState();
  const [selectionState, setSelectionState] = useState({ value: '', type: '' });
  const bottomSheetWrapRef = useRef();
  const isMobile = useIsMobile();

  const onClear = () => {
    if (filterNumberSelected === filterMap.sessions) {
      setSelectionState({
        value: sessionType,
        type: filterNumberSelected
      });
    } else {
      setSelectionState({
        value: selectedSubject,
        type: filterNumberSelected
      });
    }
    setOpenBottomSheet(false);
  };

  useClickAwayListener(bottomSheetWrapRef, () => {
    onClear();
  });

  const handleClick = (index) => () => {
    setFilterNumberSelected(index);
    if (isMobile) setOpenBottomSheet(true);
  };

  const onApply = () => {
    if (filterNumberSelected === filterMap.sessions) {
      handleSessionTypeChange(selectionState.value);
    } else {
      handleSubjectChange(selectionState.value);
    }
    setOpenBottomSheet(false);
  };

  const getFilterValue = (index) => {
    const selectedFilter =
      index === filterMap.sessions ? sessionType : selectedSubject;
    const found = filterOptions[index].options.find(
      (item) => item.value === selectedFilter
    );
    return found?.label || filterOptions[index].options[0].label;
  };

  const filterOnClick = (value) => {
    if (filterNumberSelected === filterMap.sessions) {
      handleSessionTypeChange(value);
    } else {
      handleSubjectChange(value);
    }
  };

  return (
    <>
      <Container>
        <HeaderContent>
          <PageHeaderContainer>
            <PageHeader
              className="store-batch-header"
              title="Full schedule"
              Breadcrumbs={
                <Breadcrumbs
                  fragments={[
                    { title: 'Batch page', url: `/store/batch/-/${batchUID}` },
                    {
                      title: 'Full schedule'
                    }
                  ]}
                />
              }
            />
          </PageHeaderContainer>
          <WebFilterWrapper>
            <WebFilterContainer
              filterOptions={filterOptions}
              handleClick={handleClick}
              getFilterValue={getFilterValue}
              filterOnClick={filterOnClick}
            />
            <JumpToWeek
              handleClick={handleClick}
              selectedWeek={selectedWeek}
              handleJumpToWeek={handleJumpToWeek}
              weekDetails={weekDetails}
            />
          </WebFilterWrapper>
        </HeaderContent>
        <JumpToSelector
          weekDetails={weekDetails}
          selectedWeek={selectedWeek}
          onWeekSelect={handleJumpToWeek}
        />
      </Container>
      {openBottomSheet && (
        <MwebBottomSheet
          bottomSheetWrapRef={bottomSheetWrapRef}
          filterOptions={filterOptions}
          filterNumberSelected={filterNumberSelected}
          selectionState={selectionState}
          setSelectionState={setSelectionState}
          sessionType={sessionType}
          selectedSubject={selectedSubject}
          handleSessionTypeChange={handleSessionTypeChange}
          handleSubjectChange={handleSubjectChange}
          onClear={onClear}
          onApply={onApply}
        />
      )}
    </>
  );
};

export default RecordedBatchHeader;
